import { EditOutlined } from '@ant-design/icons';
import { FormInstance, Input, InputRef } from 'antd';
import Item from 'antd/es/descriptions/Item';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { WorkspaceAccessResponse } from '../../../api/workspace';
import { SubitemIcon } from '../../../assets/CustomIcons/SubitemIcon';
import colorTokens from '../../../constants/design_system/colors';
import { useNavigateToTask } from '../../../hooks/useTaskNavigation';
import { useViewContext } from '../../../pages/Space/Context/ViewContext';
import { usePlanningSession } from '../../../serverState/planning';
import { getCurrentWorkspaceActiveSprint } from '../../../serverState/sprint';
import { useGetWorkspace } from '../../../serverState/workspace';
import { Epic } from '../../../types/Epic';
import { TaskLabel } from '../../../types/TaskLabel';
import { TaskType } from '../../../types/TaskType';
import { WorkspaceEstimationType, WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceViewEntityType } from '../../../types/WorkspaceView';
import { BaseButton } from '../../UI/BaseButton';
import { CustomDatePicker } from '../../UI/CustomDatePicker/CustomDatePicker';
import { NoDragWrapper } from '../../UI/Dropdown';
import EpicDropdown from '../../UI/Dropdown/EpicDropdown';
import EstimateDropdown from '../../UI/Dropdown/EstimateDropdown';
import LabelDropdown from '../../UI/Dropdown/LabelDropdown';
import OwnerDropdown from '../../UI/Dropdown/OwnerDropdown';
import PriorityDropdown from '../../UI/Dropdown/PriorityDropdown';
import SprintStatusDropdown from '../../UI/Dropdown/SprintStatusDropdown';
import TaskStatusDropdown from '../../UI/Dropdown/TaskStatusDropdown';
import TaskTypeDropdown from '../../UI/Dropdown/TaskTypeDropdown';

import { getPossibleSprintStatuses, getEffectiveSprintStatus, sprintStatusToStage } from '../../../utils/taskListUtils';
import { PriorityOptions } from '../helpers/helper';
import '../TaskTable.scss';

interface Item {
  key: string;
  type: string;
  title: string;
  assignee: string | null;
  statusId: number;
  epicId: string;
  sprintId: string | null;
  planningSessionId: string | null;
  priority: string;
  estimate: number | null;
  storyPoints: number | null;
  deadline: Date | null;
  startDate: Date | null;
  TaskLabels?: TaskLabel[];
}

export interface TableMetadata {
  EditableContext: React.Context<FormInstance | null>;
  epics: Epic[];
}

export interface EditableCellProps {
  title: React.ReactNode;
  status: React.ReactNode;
  priority: React.ReactNode;
  estimate: React.ReactNode;
  deadline: React.ReactNode;
  assignee: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
  handleOpen: (record: Item) => void;
  moveToPlanning: (record: Item) => void;
  usersWithAccessToSpace: WorkspaceAccessResponse[];
  activeSprintId: string | null;
  metadata: TableMetadata;
  handleEditEstimationSettings: () => void;
}

export const EditableCell: React.FC<EditableCellProps> = React.memo(
  ({
    title,
    estimate,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    handleOpen,
    moveToPlanning,
    usersWithAccessToSpace,
    metadata,
    handleEditEstimationSettings,
    ...restProps
  }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef<InputRef>(null);

    const activeSprintId = getCurrentWorkspaceActiveSprint().data?.sprint.id;
    const planningSessionId = usePlanningSession().data?.session.id;

    const workspaceQuery = useGetWorkspace();
    const framework = workspaceQuery.data?.framework;
    const estimationType = workspaceQuery.data?.estimationType ?? WorkspaceEstimationType.storyPoints;
    const viewContext = useViewContext();
    const navigateToTask = useNavigateToTask();

    let form: FormInstance<any> | undefined;
    if (metadata && metadata.EditableContext) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      form = useContext(metadata.EditableContext)!;
    }

    useEffect(() => {
      if (editing) {
        inputRef.current?.focus();
      }
    }, [editing]);

    const toggleEdit = () => {
      setEditing(!editing);
      form?.setFieldsValue({ [dataIndex]: record[dataIndex] });
    };

    const saveColumnAction = async (values: { [key: string]: any }, shouldToogleEdit = true) => {
      try {
        if (shouldToogleEdit) {
          toggleEdit();
        }

        // Filter not relevant data
        const { metadata, ...task } = record;

        if (!isEqual({ ...task, ...values }, task)) {
          if (values.assignee === '') {
            values.assignee = null;
          }
          // If change from sprint to planning user needs to confirm action
          if (task.sprintId && values.planningSessionId) {
            moveToPlanning({ ...task, ...values });
          } else {
            handleSave({ ...task, ...values });
          }
        }
      } catch (errInfo) {
        console.log('Save failed:', errInfo);
      }
    };

    const saveTaskType = (type: TaskType) => {
      void saveColumnAction({ type }, false);
    };

    let childNode = children;
    if (editable) {
      if (dataIndex === 'title') {
        childNode = (
          <div
            className="h-full ml-1 flex items-center"
            onClick={(e) => {
              if (!editing) {
                e.stopPropagation();
                handleOpen(record);
              }
            }}
          >
            {viewContext?.view &&
              viewContext?.view?.entityType === WorkspaceViewEntityType.task &&
              record.parentTaskId && <SubitemIcon className="text-neutral-600 mr-2 ml-1" />}
            <TaskTypeDropdown
              type={record.type as TaskType}
              size="small"
              onBlur={() => {
                setEditing(false);
              }}
              onSelect={(type) => {
                saveTaskType(type);
              }}
              disabled={viewContext?.view?.entityType === WorkspaceViewEntityType.epic}
              position="bottom"
            />
            {editing ? (
              <Input
                task-title-input-id={record.key}
                size={'small'}
                ref={inputRef}
                defaultValue={record[dataIndex]}
                onPressEnter={(e) => {
                  const newValue = e.target.value;
                  const trimmedNewValue = newValue.trim();
                  if (!trimmedNewValue) {
                    setEditing(false);
                    return;
                  }

                  saveColumnAction({ title: newValue });
                }}
                onBlur={(e) => {
                  const newValue = e.target.value;
                  const trimmedNewValue = newValue.trim();
                  if (!trimmedNewValue) {
                    setEditing(false);
                    return;
                  }

                  saveColumnAction({ title: newValue });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' || e.keyCode === 27) {
                    setEditing(false);
                  }
                }}
                data-no-dnd="true"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onDragStart={(e) => e.preventDefault()}
                draggable={false}
              />
            ) : (
              // </Form.Item>
              <div
                className="editable-cell-value-wrap box-border overflow-hidden inline-block"
                task-title-id={record.key}
              >
                {children}
                <div
                  task-edit-id={record.key}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(true);
                  }}
                />
                <BaseButton
                  type="secondary"
                  size="small"
                  className="open_task_details_button ml-2 text-neutral-900 absolute top-2 right-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(true);
                  }}
                >
                  <EditOutlined />
                </BaseButton>
              </div>
            )}
          </div>
        );
      } else if (dataIndex === 'assignee') {
        childNode = editing ? (
          <div className="flex justify-center items-center">
            <OwnerDropdown
              owner={
                usersWithAccessToSpace.find((userAccess) => userAccess.userId === Number(record.assignee))?.User || null
              }
              owners={usersWithAccessToSpace.map((userAccess) => userAccess.User)}
              onBlur={() => {
                setEditing(false);
              }}
              onSelect={(user) => {
                void saveColumnAction({ [dataIndex]: user?.id || null });
              }}
              size="small"
              defaultOpen={true}
              selectedElement={children}
              className="h-[35px]"
              hideOutline
              position="bottom"
            />
          </div>
        ) : (
          <div
            className="editable-cell-value-wrap flex justify-center"
            onClick={() => {
              toggleEdit();
            }}
          >
            {children}
          </div>
        );
      } else if (dataIndex === 'statusId') {
        childNode = editing ? (
          <div className="flex justify-start items-center pl-2">
            <TaskStatusDropdown
              taskStatusId={record.statusId}
              onSelect={(status) => {
                void saveColumnAction({ [dataIndex]: status.id });
              }}
              onBlur={() => {
                setEditing(false);
              }}
              size="medium"
              defaultOpen={true}
              className="h-[35px]"
              hideOutline
              position="bottom"
            />
          </div>
        ) : (
          <div className="editable-cell-value-wrap flex justify-center" onClick={toggleEdit}>
            {/* TODO: fix not reactive to changes in row state */}
            {children}
          </div>
        );
      } else if (dataIndex === 'epicId') {
        childNode = editing ? (
          <div className="w-[150px] flex justify-center items-center">
            <EpicDropdown
              epic={record.epicId ? metadata.epics.find((epic) => epic.id === Number(record.epicId)) || null : null}
              epics={metadata.epics}
              onBlur={() => {
                setEditing(false);
              }}
              onSelect={async (epic) => {
                await saveColumnAction({ [dataIndex]: epic?.id || null });
              }}
              onOpenEpic={(epic) => navigateToTask(epic)}
              size="medium"
              defaultOpen={true}
              emptyElement={<div className="text-neutral-700 px-1 py-[11px]">No epic</div>}
              showGoToIcon
              className="h-[35px]"
              hideOutline
              position="bottom"
            />
          </div>
        ) : (
          <div
            className="editable-cell-value-wrap flex justify-center"
            onClick={() => {
              if (record.parentTaskId) {
                return;
              }
              toggleEdit();
            }}
          >
            {/* TODO: fix not reactive to changes in row state */}
            {children}
          </div>
        );
      } else if (dataIndex === 'sprintId' || dataIndex === 'planningSessionId') {
        const isScrum = framework === WorkspaceFramework.scrum;
        const possibleSprintStatuses = getPossibleSprintStatuses(isScrum, activeSprintId);
        const currentSprintStatus = getEffectiveSprintStatus({ task: record as any, isScrum, activeSprintId });

        childNode = editing ? (
          <div className="flex justify-start items-center">
            <SprintStatusDropdown
              defaultOpen={true}
              sprintStatus={currentSprintStatus}
              sprintStatuses={possibleSprintStatuses.map((p) => p.sprintStatus)}
              onSelect={(sprintStatus) => {
                if (sprintStatus) {
                  const nextStage = sprintStatusToStage(sprintStatus);
                  switch (sprintStatus) {
                    case 'sprint':
                    case 'kanban':
                      void saveColumnAction({ sprintId: activeSprintId, planningSessionId: null, stage: nextStage });
                      break;

                    case 'planning':
                      void saveColumnAction({ sprintId: null, planningSessionId: planningSessionId, stage: nextStage });
                      break;

                    default:
                      void saveColumnAction({ sprintId: null, planningSessionId: null, stage: nextStage });
                      break;
                  }
                } else {
                  void saveColumnAction({ sprintId: null, planningSessionId: null });
                }
              }}
              onBlur={() => {
                setEditing(false);
              }}
              size="medium"
              className="h-[35px]"
              hideOutline
              position="bottom"
            />
          </div>
        ) : (
          <div className="editable-cell-value-wrap flex justify-start" onClick={toggleEdit}>
            {children}
          </div>
        );
      } else if (dataIndex === 'priority') {
        childNode = editing ? (
          <div className="flex justify-center items-center">
            <PriorityDropdown
              priority={record.priority as PriorityOptions}
              size="medium"
              onSelect={(priority) => {
                void saveColumnAction({ [dataIndex]: priority });
              }}
              onBlur={() => {
                setEditing(false);
              }}
              defaultOpen={true}
              className="h-[35px]"
              hideOutline
              position="bottom"
            />
          </div>
        ) : (
          <div className="flex justify-center items-center">
            <div className="editable-cell-value-wrap flex justify-center" onClick={toggleEdit}>
              {/* TODO: fix not reactive to changes in row state */}
              {children}
            </div>
          </div>
        );
      } else if (dataIndex === 'startDate') {
        childNode = editing ? (
          <div className="flex justify-center items-center">
            <NoDragWrapper>
              <CustomDatePicker
                onChange={(date) => {
                  // record[dataIndex] = date !== null ? date?.toDate() : null;
                  void saveColumnAction({
                    [dataIndex]: date !== null ? date?.toDate().toISOString() : null,
                  });
                }}
                onClose={() => {
                  setEditing(false);
                }}
                // HARDCODED AS IT SEEMS TO PASS VALUE FOR THIS FIELD AS [undefined, '14/03/2024']
                startDate={
                  children[1]
                    ? children[1].props.children !== 'No date'
                      ? dayjs(children[1].props.children, 'MMM DD, YYYY')
                      : null
                    : null
                }
                targetDate={record.deadline ? dayjs(record.deadline).toDate() : null}
                mode="startDate"
                placeholder="Set date"
                className="h-[35px]"
                hideOutline
              />
            </NoDragWrapper>
          </div>
        ) : (
          <div className="editable-cell-value-wrap flex justify-center" onClick={toggleEdit}>
            {children ? children : 'No date'}
          </div>
        );
      } else if (dataIndex === 'deadline') {
        childNode = editing ? (
          <div className="flex justify-center items-center">
            <NoDragWrapper>
              <CustomDatePicker
                onChange={(date) => {
                  // record[dataIndex] = date !== null ? date?.toDate() : null;
                  void saveColumnAction({
                    [dataIndex]: date !== null ? date?.toDate().toISOString() : null,
                  });
                }}
                onClose={() => {
                  setEditing(false);
                }}
                startDate={record.startDate ? dayjs(record.startDate).toDate() : null}
                // HARDCODED AS IT SEEMS TO PASS VALUE FOR THIS FIELD AS [undefined, '14/03/2024']
                targetDate={
                  children[1]
                    ? children[1].props.children !== 'No date'
                      ? dayjs(children[1].props.children, 'MMM DD, YYYY')
                      : null
                    : null
                }
                mode="targetDate"
                placeholder="Set date"
                className="h-[35px]"
                hideOutline
              />
            </NoDragWrapper>
          </div>
        ) : (
          <div className="editable-cell-value-wrap flex justify-center" onClick={toggleEdit}>
            {children ? children : 'No date'}
          </div>
        );
      } else if (dataIndex === 'estimate' || dataIndex === 'storyPoints') {
        childNode = editing ? (
          <div className="flex justify-center items-center">
            <EstimateDropdown
              estimate={estimationType === WorkspaceEstimationType.storyPoints ? record.storyPoints : record.estimate}
              size="small"
              onSelect={(estimate) => {
                void saveColumnAction({ [dataIndex]: estimate });
              }}
              onBlur={() => {
                setEditing(false);
              }}
              defaultOpen={true}
              onEditEstimationSettings={handleEditEstimationSettings}
              selectedElement={children}
              className="h-[35px]"
              hideOutline
              position="bottom"
            />
          </div>
        ) : (
          <div className="flex justify-center items-center">
            <div className="editable-cell-value-wrap flex justify-center" onClick={toggleEdit}>
              {/* TODO: fix not reactive to changes in row state */}
              {children}
            </div>
          </div>
        );
      } else if (dataIndex === 'TaskLabels') {
        const workspaceLabels = workspaceQuery.data?.Labels || [];

        childNode = editing ? (
          <div className="w-[125px] flex justify-start items-center pl-2">
            <LabelDropdown
              selectedLabels={
                workspaceLabels
                  .filter((label) => record.TaskLabels?.some((tl: any) => tl.labelId === label.id))
                  .sort((a, b) => {
                    return (a?.orderIndex ?? a?.id ?? 0) - (b?.orderIndex ?? b?.id ?? 0);
                  }) || []
              }
              availableLabels={workspaceLabels}
              onSelectionChange={async (labels) => {
                // Update the task labels through the API
                await saveColumnAction(
                  {
                    TaskLabels: labels.map((label) => ({ labelId: label.id, taskId: record.key })),
                  },
                  false
                );
              }}
              onBlur={() => {
                setEditing(false);
              }}
              size="small"
              defaultOpen={true}
              placeholder="No label"
              className="h-[35px]"
              hideOutline
              customAlign
            />
          </div>
        ) : (
          <div className="w-[125px] flex justify-start items-center pl-2">
            <div className="editable-cell-value-wrap flex justify-start" onClick={toggleEdit}>
              {children}
            </div>
          </div>
        );
      }
    }

    if (dataIndex === 'title' || dataIndex === undefined) {
      return <td {...restProps}>{childNode}</td>;
    }

    // Columns that should be left-aligned
    const isLeftAligned = dataIndex === 'sprintId' || dataIndex === 'planningSessionId';

    return (
      <td {...restProps}>
        <div
          className={`flex items-center h-[35px] ${isLeftAligned ? 'justify-start pl-2' : 'justify-center'}`}
          style={{
            outline: editing ? `2px solid ${colorTokens.green[300]}` : 'none',
            width: 'calc(100% - 2px)',
          }}
          onClick={() => {
            if (dataIndex === 'epicId' && record.parentTaskId) {
              return;
            }
            setEditing(true);
          }}
        >
          {childNode}
        </div>
      </td>
    );
  }
);
